import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

export interface PageBreadcrumbOverride {
  href?: string;
  label?: string;
  loading?: boolean;
}

type PageBreadcrumbOverrides = Record<string, PageBreadcrumbOverride>;

/** Overrides, tagged with the page that set them.
 *
 * Tagged rather than cleared on navigation. Clearing was an effect on the
 * provider, and effects run child-first: the incoming page published its
 * overrides before the provider wiped them, so whether a crumb survived
 * depended on whether its page re-rendered afterwards. A page whose data was
 * already cached never did, and lost its crumb. Comparing the pathname instead
 * makes a stale override unreadable rather than racing to delete it.
 */
interface PageBreadcrumbState {
  pathname: string;
  overrides: PageBreadcrumbOverrides;
}

interface PageBreadcrumbContextValue {
  /** The current pathname, read once here so a page publishing overrides
   * does not need a router of its own: outside a provider the hook is a
   * no-op, and page tests rely on that. */
  pathname: string;
  state: PageBreadcrumbState;
  setState: React.Dispatch<React.SetStateAction<PageBreadcrumbState>>;
}

const EMPTY_OVERRIDES: PageBreadcrumbOverrides = {};

const PageBreadcrumbContext = createContext<PageBreadcrumbContextValue | null>(
  null
);

export const PageBreadcrumbProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { pathname } = useLocation();
  const [state, setState] = useState<PageBreadcrumbState>({
    pathname: "",
    overrides: EMPTY_OVERRIDES,
  });

  const value = useMemo(
    () => ({ pathname, state, setState }),
    [pathname, state, setState]
  );

  return (
    <PageBreadcrumbContext.Provider value={value}>
      {children}
    </PageBreadcrumbContext.Provider>
  );
};

export const usePageBreadcrumbs = (overrides: PageBreadcrumbOverrides) => {
  const context = useContext(PageBreadcrumbContext);
  const pathname = context?.pathname;
  const serializedOverrides = JSON.stringify(overrides);
  const stableOverrides = useMemo(() => overrides, [serializedOverrides]);
  const setState = context?.setState;

  useEffect(() => {
    if (!setState || pathname === undefined) return;

    setState({ pathname, overrides: stableOverrides });
  }, [setState, pathname, stableOverrides]);
};

export const useResolvedPageBreadcrumbOverrides = () => {
  const context = useContext(PageBreadcrumbContext);

  if (!context || context.state.pathname !== context.pathname) {
    return EMPTY_OVERRIDES;
  }

  return context.state.overrides;
};
